import { AJUDA_PRODUCAO } from "@/lib/ajuda/dialogos/producao";
import { useEffect, useState } from "react";
import { ArrowRight, Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth";
import { usePermissao } from "@/lib/use-permissao";
import { Link } from "react-router";
import { ProsthesisTeethPicker } from "@/components/prosthesis/ProsthesisTeethPicker";
import { CampoDeUnidades } from "./CampoDeUnidades";
import { SingleSelectButtons } from "@/components/prosthesis/SingleSelectButtons";
import { ServicosAgrupados } from "@/components/prosthesis/ServicosAgrupados";
import { ShadeSelector } from "@/components/prosthesis/ShadeSelector";
import {
  FDI_LOWER_ARCH,
  FDI_UPPER_ARCH,
  formatarDentes,
  parseTeethToFdi,
} from "@/components/prosthesis/fdi-teeth";
import { quantidadePadrao } from "@bookdental/schemas";

const dinheiro = (cents: number | null | undefined) =>
  cents == null ? "—" : (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * Acha, na lista do catálogo, a opção que corresponde ao item já gravado no serviço.
 *
 * O pedido guarda o item que a CLÍNICA usou, que costuma ser o clone da organização dela
 * (`originGlobalId` apontando pro item global), enquanto o catálogo aqui vem do global. Comparar só
 * por `id` não achava nada — e o `<select>` antigo então caía na primeira opção, propondo trocar o
 * serviço sem ninguém ter pedido. Casar pelas duas pontas resolve nos dois sentidos.
 */
function idNoCatalogo(atual: any | null | undefined, opcoes: any[]): string | null {
  if (!atual) return null;
  const achou = opcoes.find(
    (o) =>
      o.id === atual.id ||
      o.id === atual.originGlobalId ||
      o.originGlobalId === atual.id ||
      (!!o.originGlobalId && o.originGlobalId === atual.originGlobalId),
  );
  return achou?.id ?? null;
}

/**
 * O laboratório edita UM serviço já recebido. Mesma anatomia do "Novo pedido" — odontograma à
 * esquerda, campos à direita — porque é a mesma decisão, só que sobre algo que já existe. Cada
 * campo mostra o valor original ao lado do novo, então o que está mudando fica visível enquanto
 * se edita.
 *
 * A alteração vale na hora, sem aceite da clínica (decisão do beta). O que fica é o registro:
 * cada campo alterado vira uma linha em `ProsthesisServiceAlteracao`, com antes, depois, quem e
 * quando — invisível na interface por enquanto.
 */
export function EditarServicoDialog({
  aberto,
  servico,
  onFechar,
}: {
  aberto: boolean;
  /** O serviço a editar, no estado atual — a base do "original". */
  servico: any | null;
  onFechar: () => void;
}) {
  const utils = trpc.useUtils();
  const podeCatalogo = usePermissao("labCatalog.manage");
  const [categoriaId, setCategoriaId] = useState("");
  const [materialId, setMaterialId] = useState<string | null>(null);
  const [corId, setCorId] = useState<string | null>(null);
  const [obs, setObs] = useState("");
  const [selectedFdi, setSelectedFdi] = useState<string[]>([]);
  const [dentesTocados, setDentesTocados] = useState(false);
  /** Nulo = acompanha os dentes (se mexidos) ou fica a do pedido; número = a pessoa digitou. */
  const [quantidadeDigitada, setQuantidadeDigitada] = useState<number | null>(null);
  const [odontogramKey, setOdontogramKey] = useState(0);

  // Abre já com o estado atual do serviço: alterar é partir do que existe, não de um formulário
  // em branco. O odontograma nasce com os dentes do pedido marcados.
  //
  // O catálogo é o DO LABORATÓRIO (`labOrganizationId`), nunca o molde global: é o mesmo que a
  // clínica viu ao montar o pedido. Sem isso a lista vinha com os ids globais, e salvar sem tocar
  // no campo Serviço re-apontava o serviço pro molde e gravava uma alteração com antes == depois —
  // além de esconder o que o laboratório personalizou e mostrar o que ele desativou.
  const labOrganizationId = useAuth().user?.activeOrganization?.id;
  const categoriasQuery = trpc.prosthesis.catalog.serviceCategories.useQuery(
    { labOrganizationId },
    { enabled: aberto && !!labOrganizationId },
  );
  const coresQuery = trpc.prosthesis.catalog.shadeOptions.useQuery(
    { labOrganizationId },
    { enabled: aberto && !!labOrganizationId },
  );
  const materiaisQuery = trpc.prosthesis.catalog.materialOptions.useQuery(
    { serviceCategoryId: categoriaId },
    { enabled: aberto && !!categoriaId },
  );
  // Item que o laboratório desativou depois (ou vindo de pedido antigo) não aparece na lista —
  // sem reincluí-lo, ele sumiria da grade, o campo ficaria sem valor e o envio travaria em
  // silêncio, já que serviço é obrigatório.
  const comOriginal = (opcoes: any[], atual: any | null | undefined) =>
    !atual || idNoCatalogo(atual, opcoes) ? opcoes : [atual, ...opcoes];

  const categorias = comOriginal(categoriasQuery.data ?? [], servico?.serviceCategory);
  const cores = comOriginal(coresQuery.data ?? [], servico?.shadeOption);
  const materiais = materiaisQuery.data ?? [];

  // Semear depois que o catálogo chega: o item gravado no serviço pode ser um clone da clínica, e
  // só dá pra apontar a opção equivalente com a lista em mãos.
  const catalogoPronto = (categoriasQuery.data?.length ?? 0) > 0 && (coresQuery.data?.length ?? 0) > 0;
  const categoriaOriginalId = idNoCatalogo(servico?.serviceCategory, categorias);
  const corOriginalId = idNoCatalogo(servico?.shadeOption, cores);
  useEffect(() => {
    if (!aberto || !servico || !catalogoPronto) return;
    setCategoriaId(categoriaOriginalId ?? "");
    setCorId(corOriginalId);
    setObs(servico.notes ?? "");
    setSelectedFdi(parseTeethToFdi(servico.teeth));
    setDentesTocados(false);
    setQuantidadeDigitada(null);
    setOdontogramKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, servico?.id, catalogoPronto]);

  // Material depende da categoria, então chega depois — semeado no seu próprio efeito.
  const materialOriginalId = idNoCatalogo(servico?.materialOption, materiais);
  useEffect(() => {
    if (!aberto || !servico || materiais.length === 0) return;
    setMaterialId(materialOriginalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, servico?.id, materiais.length]);

  const salvar = trpc.prosthesis.changes.updateService.useMutation({
    onSuccess: (r: any) => {
      utils.prosthesis.incoming.list.invalidate();
      utils.prosthesis.changes.invalidate();
      utils.prosthesis.list.invalidate();
      // A quantidade muda o valor do serviço: faturamento e extrato também.
      utils.financeiro.invalidate();
      toast.success(`Serviço atualizado (${r?.alteracoes ?? 0} campo(s) alterado(s)).`);
      onFechar();
    },
    onError: (e) => toast.error(e.message || "Erro ao salvar a alteração."),
  });

  if (!servico) return null;

  // Mesma regra do formulário de novo pedido — arcada inteira vira rótulo, não a lista de 32
  // dentes, que estoura o limite do campo e fazia o salvamento voltar 400.
  //
  // Enquanto ninguém mexe no odontograma, o valor proposto é o TEXTO ORIGINAL, não o que o
  // odontograma conseguiu ler dele: o campo aceita texto livre no pedido ("24-26"), que
  // `parseTeethToFdi` não entende — e salvar qualquer outra edição apagava esse dado clínico sem
  // ninguém pedir.
  const dentesIlegiveis = !!servico?.teeth && parseTeethToFdi(servico.teeth).length === 0;
  const dentesPropostos = dentesTocados ? formatarDentes(selectedFdi) : (servico?.teeth ?? null);

  const marcarDentes = (fdi: string[]) => {
    setSelectedFdi(fdi);
    setDentesTocados(true);
  };

  const original = {
    servico: servico.serviceCategory?.label ?? "—",
    material: servico.materialOption?.label ?? null,
    cor: servico.shadeOption?.code ?? null,
    dentes: servico.teeth ?? null,
    obs: servico.notes ?? null,
    quantidade: String(servico.quantidade ?? 1),
    preco: servico.priceCents ?? null,
  };
  const abrangencia = categorias.find((c: any) => c.id === categoriaId)?.abrangencia;
  const quantidadeProposta =
    quantidadeDigitada ?? (dentesTocados ? quantidadePadrao(dentesPropostos, abrangencia) : (servico.quantidade ?? 1));
  const proposto = {
    servico: categorias.find((c: any) => c.id === categoriaId)?.label ?? original.servico,
    material: materiais.find((m: any) => m.id === materialId)?.label ?? null,
    cor: cores.find((c: any) => c.id === corId)?.code ?? null,
    dentes: dentesPropostos,
    quantidade: String(quantidadeProposta),
    obs: obs || null,
  };

  const mudou =
    proposto.servico !== original.servico ||
    proposto.material !== original.material ||
    proposto.cor !== original.cor ||
    proposto.dentes !== original.dentes ||
    proposto.quantidade !== original.quantidade ||
    proposto.obs !== original.obs;

  const toggleArch = (arch: string[], isSelected: boolean) => {
    setSelectedFdi((prev) =>
      isSelected ? prev.filter((f) => !arch.includes(f)) : Array.from(new Set([...prev, ...arch])),
    );
    setDentesTocados(true);
    setOdontogramKey((k) => k + 1);
  };

  const enviar = () => {
    if (!mudou || salvar.isPending || !categoriaId) return;
    salvar.mutate({
      serviceId: servico.id,
      serviceCategoryId: categoriaId,
      materialOptionId: materialId,
      shadeOptionId: corId,
      teeth: dentesPropostos,
      quantidade: quantidadeProposta,
      notes: obs.trim() || null,
    });
  };

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[92dvh] max-w-5xl flex-col gap-0 overflow-hidden border-zinc-200 bg-white p-0 shadow-xl dark:border-border dark:bg-card"
      >
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-foreground" ajuda={AJUDA_PRODUCAO.editarServico}>
            <PenLine className="h-4 w-4 text-zinc-500" />
            Editar serviço
          </DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            A alteração vale na hora. O que mudou fica registrado com data e autor.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <ProsthesisTeethPicker
            selectedFdi={selectedFdi}
            odontogramKey={odontogramKey}
            onChange={marcarDentes}
            onToggleArch={toggleArch}
          />

          <div className="flex min-w-0 flex-1 flex-col gap-3">
            {/* Mesmos controles do pedido, e não `<select>`: cor tem dezenas de códigos por sistema
                e num dropdown vira uma lista impossível de comparar. A escolha ORIGINAL fica
                marcada em âmbar na própria grade — a comparação "era isto → virou aquilo" acontece
                onde se escolhe, e dá pra reescolher a original com um clique. */}
            <CampoComOriginal rotulo="Serviço" original={original.servico} proposto={proposto.servico}>
              <ServicosAgrupados
                servicos={categorias as { id: string; label: string; group: string | null }[]}
                selectedId={categoriaId}
                originalId={categoriaOriginalId}
                // Serviço é obrigatório: clicar no já escolhido não desmarca, só reafirma.
                onSelect={(id) => setCategoriaId(id)}
              />
            </CampoComOriginal>

            <CampoComOriginal rotulo="Material" original={original.material} proposto={proposto.material}>
              <SingleSelectButtons
                options={materiais.map((m: any) => ({ id: m.id, label: m.label }))}
                selectedId={materialId}
                originalId={materialOriginalId}
                onSelect={(id) => setMaterialId((prev) => (prev === id ? null : id))}
              />
              {materiais.length === 0 && (
                <p className="mt-1 text-[11px] text-zinc-400 dark:text-muted-foreground">
                  Nenhum material compatível cadastrado pra esse serviço.
                  {podeCatalogo && (
                    <>
                      {" "}
                      <Link to="/lab/catalogo/gerenciar" className="font-semibold text-brand-700 hover:underline dark:text-primary">
                        Cadastrar no Catálogo
                      </Link>
                    </>
                  )}
                </p>
              )}
            </CampoComOriginal>

            <CampoComOriginal rotulo="Cor" original={original.cor} proposto={proposto.cor}>
              <ShadeSelector
                shades={cores}
                selectedId={corId}
                originalId={corOriginalId}
                onSelect={(id) => setCorId((prev) => (prev === id ? null : id))}
              />
            </CampoComOriginal>

            <CampoComOriginal rotulo="Dentes" original={original.dentes} proposto={proposto.dentes}>
              <div className="flex items-center gap-2">
                <p className="min-h-9 flex-1 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-2 text-[13px] text-zinc-700 dark:border-border dark:bg-muted/40 dark:text-foreground">
                  {dentesPropostos ?? "Nenhum selecionado"}
                </p>
                {(selectedFdi.length > 0 || dentesPropostos) && (
                  <button
                    type="button"
                    onClick={() => {
                      marcarDentes([]);
                      setOdontogramKey((k) => k + 1);
                    }}
                    className="shrink-0 text-[11px] font-medium text-brand-600 hover:underline dark:text-primary"
                  >
                    Limpar
                  </button>
                )}
              </div>
              {dentesIlegiveis && !dentesTocados && (
                <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">
                  Os dentes vieram como texto livre no pedido e o odontograma não consegue
                  desenhá-los. Eles ficam como estão até você escolher no odontograma.
                </p>
              )}
              <div className="mt-1 flex gap-1.5">
                <BotaoArcada rotulo="Superior" arch={FDI_UPPER_ARCH} selectedFdi={selectedFdi} onToggle={toggleArch} />
                <BotaoArcada rotulo="Inferior" arch={FDI_LOWER_ARCH} selectedFdi={selectedFdi} onToggle={toggleArch} />
              </div>
            </CampoComOriginal>

            <CampoComOriginal rotulo="Unidades" original={original.quantidade} proposto={proposto.quantidade}>
              <CampoDeUnidades
                valor={quantidadeProposta}
                dentes={dentesPropostos}
                abrangencia={abrangencia}
                ajustada={quantidadeDigitada != null}
                onMudar={setQuantidadeDigitada}
              />
            </CampoComOriginal>

            <CampoComOriginal rotulo="Observação" original={original.obs} proposto={proposto.obs}>
              <input value={obs} onChange={(e) => setObs(e.target.value)} className={inputCls} />
            </CampoComOriginal>

            {original.preco != null && (
              <p className="flex items-center gap-1.5 rounded-md bg-zinc-50 px-2.5 py-2 text-[13px] dark:bg-muted/40">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Valor</span>
                <span className="font-semibold text-zinc-900 dark:text-foreground">
                  {quantidadeProposta > 1 ? `${quantidadeProposta} × ${dinheiro(original.preco)} = ${dinheiro(original.preco * quantidadeProposta)}` : dinheiro(original.preco)}
                </span>
                <span className="text-zinc-500 dark:text-muted-foreground">
                  · o preço unitário combinado no envio não muda; o total acompanha as unidades
                </span>
              </p>
            )}
          </div>
        </div>

        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-6 py-3 dark:border-border">
          {!mudou && (
            <p className="mr-auto text-xs text-zinc-500 dark:text-muted-foreground">
              Altere algum campo para salvar.
            </p>
          )}
          <button
            type="button"
            onClick={onFechar}
            className="inline-flex h-11 items-center justify-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 sm:h-9 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!mudou || salvar.isPending}
            onClick={enviar}
            className="inline-flex h-11 items-center justify-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60 sm:h-9"
          >
            {salvar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Salvar alteração
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-[13px] text-zinc-800 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";

/** Campo com o valor ORIGINAL sempre à vista — riscado quando o proposto difere. É o que evita
 *  descobrir só no aceite que algo mudou sem querer. */
function CampoComOriginal({
  rotulo,
  original,
  proposto,
  children,
}: {
  rotulo: string;
  original: string | null;
  proposto: string | null;
  children: React.ReactNode;
}) {
  const mudou = (original ?? "—") !== (proposto ?? "—");
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-baseline gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
          {rotulo}
        </span>
        <span
          className={cn(
            "text-[11px]",
            mudou ? "text-zinc-400 line-through dark:text-muted-foreground/70" : "text-zinc-400 dark:text-muted-foreground/70",
          )}
        >
          {original ?? "—"}
        </span>
        {mudou && (
          <>
            <ArrowRight className="h-3 w-3 shrink-0 text-amber-500" />
            <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-400">{proposto ?? "—"}</span>
          </>
        )}
      </div>
      {children}
    </div>
  );
}

function BotaoArcada({
  rotulo,
  arch,
  selectedFdi,
  onToggle,
}: {
  rotulo: string;
  arch: string[];
  selectedFdi: string[];
  onToggle: (arch: string[], isSelected: boolean) => void;
}) {
  const ativa = arch.every((f) => selectedFdi.includes(f));
  return (
    <button
      type="button"
      onClick={() => onToggle(arch, ativa)}
      className={cn(
        "inline-flex h-8 flex-1 items-center justify-center rounded-md border px-2 text-[11px] font-medium transition-colors",
        ativa
          ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
          : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-300 dark:border-border dark:bg-card dark:text-muted-foreground",
      )}
    >
      Arcada {rotulo}
    </button>
  );
}
